/**
 * Use customer account route.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { useEffect, useMemo, useState } from 'react'
import { resolveNextPath } from '../helpers/formatters'
import { CUSTOMER_ACCOUNT_BASE_SLUG, normalizePageSlug } from '../routes'
import { useShopI18n } from '../../i18n'

export type CustomerAccountRouteState = {
  nextPath: string
  nextPathReady: boolean
  normalizedSlug: string
  nestedPath: string
  isDashboardRoute: boolean
  isOrdersListRoute: boolean
  isOrderDetailRoute: boolean
  isSupportRoute: boolean
  isSupportDetailRoute: boolean
  isPrivacyRoute: boolean
  isProfileRoute: boolean
  isAddressesRoute: boolean
  isPreferencesRoute: boolean
  orderDetailId: string
  supportDetailId: string
  requestedAccountPath: string
  checkoutResult: 'success' | 'cancelled' | null
  checkoutSessionId: string
}

/**
 * Use customer account route.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const useCustomerAccountRoute = (currentSlug: string): CustomerAccountRouteState => {
  const { toLocalizedPath } = useShopI18n()
  const [search, setSearch] = useState('')
  const [nextPathReady, setNextPathReady] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    setSearch(window.location.search)
    setNextPathReady(true)
  }, [])

  const nextPath = resolveNextPath(toLocalizedPath('/dashboard'), search)

  return useMemo(() => {
    const decodeRouteSegment = (value: string) => {
      try {
        return decodeURIComponent(value)
      } catch {
        return value
      }
    }

    const normalizedSlug = normalizePageSlug(currentSlug)
    const normalizedDashboardSlug = normalizedSlug
      .replace(/^accounts?(?=\/|$)/, CUSTOMER_ACCOUNT_BASE_SLUG)
      .replace(/^\/+/, '')
    const baseSlug = CUSTOMER_ACCOUNT_BASE_SLUG
    const nestedPath = normalizedDashboardSlug.replace(baseSlug, '').replace(/^\/+/, '')
    const normalizedNestedPath = nestedPath
      .replace(/^shop-orders(?=\/|$)/, 'orders')
      .replace(/^support-inbox(?=\/|$)/, 'support')
    const nestedSegments = normalizedNestedPath
      .split('/')
      .map((segment) => segment.trim())
      .filter(Boolean)
    const query = new URLSearchParams(search)
    const orderDetailIdFromQuery = query.get('orderId')?.trim() ?? ''
    const checkoutResultFromQuery = query.get('checkout')?.trim() ?? ''
    const checkoutSessionId = query.get('session_id')?.trim() ?? ''

    const isDashboardRoute = nestedSegments.length === 0
    const isOrdersListRoute = nestedSegments.length === 1 && nestedSegments[0] === 'orders'
    const isOrderDetailRoute =
      (nestedSegments.length === 2 && nestedSegments[0] === 'orders') ||
      (isOrdersListRoute && orderDetailIdFromQuery.length > 0)
    const isSupportRoute = nestedSegments.length === 1 && nestedSegments[0] === 'support'
    const isSupportDetailRoute = nestedSegments.length === 2 && nestedSegments[0] === 'support'
    const isPrivacyRoute = nestedSegments.length === 1 && nestedSegments[0] === 'privacy'
    const isProfileRoute = nestedSegments.length === 1 && nestedSegments[0] === 'profile'
    const isAddressesRoute = nestedSegments.length === 1 && nestedSegments[0] === 'addresses'
    const isPreferencesRoute = nestedSegments.length === 1 && nestedSegments[0] === 'preferences'
    const orderDetailId = isOrderDetailRoute
      ? decodeRouteSegment(nestedSegments[1] || orderDetailIdFromQuery)
      : ''
    const supportDetailId = isSupportDetailRoute ? decodeRouteSegment(nestedSegments[1] ?? '') : ''
    const requestedAccountPath = toLocalizedPath(
      normalizedDashboardSlug ? `/${normalizedDashboardSlug}` : '/dashboard',
    )
    const checkoutResult =
      checkoutResultFromQuery === 'success'
        ? 'success'
        : checkoutResultFromQuery === 'cancelled'
          ? 'cancelled'
          : null

    return {
      nextPath,
      nextPathReady,
      normalizedSlug,
      nestedPath: normalizedNestedPath,
      isDashboardRoute,
      isOrdersListRoute,
      isOrderDetailRoute,
      isSupportRoute,
      isSupportDetailRoute,
      isPrivacyRoute,
      isProfileRoute,
      isAddressesRoute,
      isPreferencesRoute,
      orderDetailId,
      supportDetailId,
      requestedAccountPath,
      checkoutResult,
      checkoutSessionId,
    }
  }, [currentSlug, nextPath, nextPathReady, search, toLocalizedPath])
}
